import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChannelConstant } from '@components/chat/chat-list-header/constants/channels.constant';
import { UserHelperService } from '@helpers/user-helper.service';
import { TabulationI } from '@interfaces/tabulation.interface';
import { UserSocketLoginI } from '@interfaces/user.interface';
import { TranslateService } from '@ngx-translate/core';
import { MaskApplierService } from '@node_modules/ngx-mask';
import { TreeSelect } from '@node_modules/primeng/treeselect/treeselect';
import { InputDataTypeEndChatDialog, InputDataTypeEndChatDialogTabulation } from '@omnitypes/end-attendance.type';
import { phoneMaskNoBRPrefix } from '@shared/scripts/general';
import { ToastrService } from 'ngx-toastr';
import { PrimeIcons, TreeNode } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { ChannelE } from '@interfaces/channel.interface';
import { ParametersHttpService } from '@services/parameters/parameters-http.service';
import { ParameterTypeE } from '@enums/parameter-type.enum';
import { TabulationsByChannelEntry } from '@interfaces/tabulations-by-channel-entry.interface';
import { ChatSummaryService } from '@services/chat-summary/chat-summary.service';

type NodeItem = {
  channelId: string;
  nodes: TreeNode<TabulationI>[];
  selectedTabulations: TreeNode<TabulationI>[] | TreeNode<TabulationI> | null;
  commenter: string;
  chatsCount?: number;
  channelType?: string;
  channelNumber?: string;
  iconChannel?: string;
};

@Component({
  selector: 'app-end-chat-dialog',
  templateUrl: './end-chat-dialog.component.html',
  styleUrls: ['./end-chat-dialog.component.scss'],
})
export class EndChatDialogDialogComponent implements OnInit, AfterViewInit {
  @ViewChildren('treeSelect') treeSelects!: QueryList<ElementRef<TreeSelect>>;
  public message!: InputDataTypeEndChatDialog;
  public tabulations: InputDataTypeEndChatDialogTabulation[] = [];
  private user!: UserSocketLoginI;

  public treeNodesTabulation: NodeItem[] = [];

  public iconChannel: string = '';

  public tabulationSelectionMode = 'single';
  public generatingSummary = false;

  constructor(
    private dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) private data: InputDataTypeEndChatDialog,
    private userHelperService: UserHelperService,
    private toaster: ToastrService,
    private translate: TranslateService,
    private renderer: Renderer2,
    private maskService: MaskApplierService,
    private readonly parametersHttpService: ParametersHttpService,
    private readonly chatSummaryService: ChatSummaryService
  ) {
    this.tabulations = this.data.tabulations ?? [];
    this.getUserLogged();
  }

  ngOnInit() {
    this.message = this.data;
    const channel = this.data.chat?.channel;
    this.iconChannel = ChannelConstant.find((type) => type.name === channel?.type)?.icon ?? '';
    this.loadTabulations();
    this.checkAllowMultipleTabulations();
  }

  ngAfterViewInit(): void {
    for (const treeSelect of this.treeSelects) {
      const treeSelectElement = treeSelect.nativeElement;
      this.renderer.listen(treeSelectElement, 'click', (event: MouseEvent) => {
        this.onTreeItemClick(event);
      });
    }
  }

  private async checkAllowMultipleTabulations() {
    const parameter = await firstValueFrom(this.parametersHttpService.allowMultipleTabulationsParameter$);
    if (parameter?.parameter?.allowMultipleTabulationsOption) {
      this.tabulationSelectionMode = 'checkbox';
    }
  }

  private findRootParent(node_: HTMLElement) {
    let node = node_;
    let tagName = node.tagName.toLowerCase().trim();
    while (tagName !== 'p-treeselect') {
      if (node.parentElement) {
        node = node.parentElement;
        tagName = node.tagName.toLowerCase().trim();
      } else {
        break;
      }
    }
    return node;
  }

  private onTreeItemClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;

    if (targetElement.classList.contains('p-tree-toggler-icon')) {
      return;
    }

    const clickedNode = this.getClickedNode(targetElement);
    if (clickedNode) {
      const root = this.findRootParent(targetElement);

      const channelId = root.id;
      const nodeLabel = clickedNode.querySelector('.p-treenode-label')?.textContent?.trim();
      if (nodeLabel) {
        const item = this.treeNodesTabulation.find((item) => item.channelId === channelId);
        const node = this.findTabulationByLabel(item?.nodes ?? [], nodeLabel);

        if (node && !node.selectable) {
          const selectedTabulations = item?.selectedTabulations;
          if (selectedTabulations) {
            item.selectedTabulations = [];
          }
          node.expanded = !node.expanded;
          //Ensuring Angular knows it was updated;
          this.treeNodesTabulation = [...this.treeNodesTabulation];
        }
      }
    }
  }

  findTabulationByLabel(nodes: TreeNode[], label: string): TreeNode | undefined {
    for (let node of nodes) {
      if (node.label === label) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const foundInChildren = this.findTabulationByLabel(node.children, label);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return undefined;
  }

  getClickedNode(target: HTMLElement): HTMLElement | null {
    while (target && !target.classList.contains('p-treenode')) {
      target = target.parentElement!;
    }
    return target;
  }

  public async close(action?: string) {
    const fromCalls = !this.data.isChat;
    const canValidate = action != 'cancel' || fromCalls;
    const isMandatoryTabulation = await this.isMandatoryTabulationRequired();
    if (canValidate && isMandatoryTabulation) {
      if (isMandatoryTabulation) {
        const translates = await firstValueFrom(
          this.translate.get(['MANDATORY_TABULATION_CHAT_ERROR', 'MANDATORY_TABULATION_TELEPHONY_ERROR'])
        );
        const isMessaging = this.data.isChat;
        const message = isMessaging
          ? translates.MANDATORY_TABULATION_CHAT_ERROR
          : translates.MANDATORY_TABULATION_TELEPHONY_ERROR;
        this.toaster.error(message, translates.MANDATORY_TABULATION, {
          closeButton: true,
        });
        return;
      }
    }
    this.dialogRef.close(action);
  }

  public async confirm() {
    if (await this.canClose()) {
      const tabulationsByChannel: TabulationsByChannelEntry[] = [];
      if (this.treeNodesTabulation.length) {
        for (const node of this.treeNodesTabulation) {
          const selectedTabulations = Array.isArray(node.selectedTabulations)
            ? node.selectedTabulations
            : [node.selectedTabulations];
          tabulationsByChannel.push({
            channelId: node.channelId,
            tabulations: selectedTabulations
              .filter(Boolean)
              .filter((item) => !!item?.data)
              .map((item) => item?.data!),
            commenter: node.commenter,
          });
        }
      }

      return this.dialogRef.close(tabulationsByChannel);
    }
  }

  public hasTabulationSelected() {
    return this.treeNodesTabulation.some((item) => {
      const selected = item.selectedTabulations;
      const selectedArray = Array.isArray(selected) ? selected : selected ? [selected] : [];
      return selectedArray.length > 0;
    });
  }

  async canClose() {
    if (!this.hasTabulations() && !(await this.isMandatoryTabulationRequired())) {
      return true;
    }

    if (this.data.mandatoryTabulationParameterResponse) {
      const isMessaging = this.data.isChat;
      const isMandatoryTabulation = await this.isMandatoryTabulationRequired();

      if (isMandatoryTabulation && !this.hasTabulationSelected()) {
        const translates = await firstValueFrom(
          this.translate.get(['MANDATORY_TABULATION_CHAT_ERROR', 'MANDATORY_TABULATION_TELEPHONY_ERROR'])
        );

        const message = isMessaging
          ? translates.MANDATORY_TABULATION_CHAT_ERROR
          : translates.MANDATORY_TABULATION_TELEPHONY_ERROR;

        this.toaster.error(message, translates.MANDATORY_TABULATION, {
          closeButton: true,
        });
        return false;
      }
    }
    return true;
  }

  private async isMandatoryTabulationRequired() {
    const isMessaging = this.data.isChat;
    const isTelephony = !this.data.isChat;

    if (!this.data.mandatoryTabulationParameterResponse) {
      return;
    }
    const { parameter } = this.data.mandatoryTabulationParameterResponse;
    const isMandatoryTabulation =
      parameter.mandatoryTabulationOption === 'messaging_and_telephony' ||
      (isMessaging && parameter.mandatoryTabulationOption === 'messaging') ||
      (isTelephony && parameter.mandatoryTabulationOption === 'telephony');

    return isMandatoryTabulation;
  }

  private getUserLogged(): void {
    const user = this.userHelperService.getSocketLoginData();
    if (!user) return;
    this.user = user;
  }

  private async loadTabulations() {
    if (this.tabulations?.length) {
      for (const item of this.tabulations) {
        const iconChannel = ChannelConstant.find((type) => type.name === item.channelType)?.icon ?? '';
        const newNode: NodeItem = {
          nodes: [],
          channelId: item.channelId,
          selectedTabulations: null,
          commenter: '',
          channelNumber: item.channelNumber ? phoneMaskNoBRPrefix(this.maskService, item.channelNumber) : undefined,
          channelType: item.channelType
            ? this.translate.instant(this.channelTypeForTranslate(item.channelType)?.toUpperCase())
            : '',
          chatsCount: item.chatsCount,
          iconChannel,
        };

        this.treeNodesTabulation.push(newNode);
        newNode.nodes = item.tabulations.map((tabulation) => {
          return this.loadNodesTabulation(tabulation, item.channelId!);
        });

        this.setSelectable(newNode.nodes);
      }
    }
  }

  private channelTypeForTranslate(type: string): string {
    if (type === ChannelE.FACEBOOK) return 'FACEBOOK';
    if (type === ChannelE.WHATSAPP_OFICIAL) return 'WHATSAPP_OFICIAL';
    if (type === ChannelE.INSTAGRAM) return 'INSTAGRAM';
    return type;
  }

  private loadNodesTabulation(tabulation: TabulationI, channelId: string): TreeNode {
    const nodeTabulation: TreeNode = {
      label: tabulation.name,
      data: tabulation,
      key: tabulation._id,
      expanded: false,
    };

    if (this.hasTabulationsDaughters(tabulation)) {
      nodeTabulation.icon = PrimeIcons.BOOKMARK_FILL;
      nodeTabulation.children = [];
      tabulation.tabulationsDaughters?.forEach((tabulationDaughter) => {
        nodeTabulation.children?.push(this.loadNodesTabulation(tabulationDaughter, channelId));
      });
    } else {
      nodeTabulation.icon = PrimeIcons.BOOKMARK;
    }
    return nodeTabulation;
  }

  setSelectable(nodes: any[]): void {
    nodes.forEach((node) => {
      if (!node.children || node.children.length === 0) {
        node.selectable = true;
      } else {
        node.selectable = false;
        if (node.children) {
          this.setSelectable(node.children);
        }
      }
    });
  }
  public async generateSummary(item: NodeItem): Promise<void> {
    const chatId = this.data.chat?._id;
    if (!chatId || this.generatingSummary) return;
    this.generatingSummary = true;
    try {
      const response = await firstValueFrom(this.chatSummaryService.generateSummary(chatId));
      item.commenter = response.summary;
    } catch {
      this.toaster.error(this.translate.instant('GENERATE_SUMMARY_ERROR'), undefined, { closeButton: true });
    } finally {
      this.generatingSummary = false;
    }
  }

  public hasTabulations(): boolean {
    let sumLen = 0;
    for (const item of this.tabulations ?? []) {
      sumLen += item.tabulations.length ?? 0;
    }
    return sumLen > 0;
  }

  public hasTabulationsDaughters(tabulation: TabulationI): boolean {
    return !!tabulation.tabulationsDaughters && tabulation.tabulationsDaughters.length > 0;
  }

  public textEmptyMessageFieldTabulation(): string {
    return this.translate.instant('TABULATION_NO_DATA');
  }
}
